import { Box, BoxProps, Flex, Grid } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Transition } from 'react-transition-group'

import { usePersisted } from '../../store/appStore/persistence/usePersistence'
import { DoubleChevronIcon } from '../Icons/DoubleChevronIcon'

export type SecondarySidebarProps = BoxProps
export const cubicBezierFunction = 'cubic-bezier(0.87, 0, 0.13, 1)'

const PANEL_WIDTH = '250px'
const panelBezierTransition = `width 250ms ${cubicBezierFunction}`

const panelTransition: any = {
  entering: {
    width: PANEL_WIDTH,
    transition: panelBezierTransition,
  },
  entered: {
    width: PANEL_WIDTH,
    transition: panelBezierTransition,
  },
  exiting: { width: PANEL_WIDTH },
  exited: {
    width: '0px',
    transition: panelBezierTransition,
  },
}

const buttonTransition: any = {
  entered: {
    height: '100px',
  },
}

/**
 *  TODO: the animations controlled here have a timeline, and should use a state machine
 * rather than just react state. A reducer, or more advanced tool would make it easier.
 *
 * Simple transitions don't cover all the states needed.
 */

export const SecondarySidebar = ({
  children,
  ...props
}: SecondarySidebarProps) => {
  const { pathname } = useLocation()

  const [isOpen, setIsOpen] = usePersisted({
    key: `${pathname}_secondarySidebarIsOpen`,
    value: true,
    where: 'localstorage',
    until: 'login',
  })

  const [hasSeenToggleAnimation, setHasSeenToggleAnimation] = usePersisted({
    key: `secondarySidebarHasSeenToggleAnimation`,
    value: false,
    where: 'memory',
    until: 'login',
  })

  const [isToggleButtonSmall, setIsToggleButtonSmall] = useState(true)
  const nodeRef = useRef(null)

  const toggleExpand = () => {
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    const timerId = setTimeout(() => {
      setIsToggleButtonSmall(isOpen!)

      if (!isOpen) {
        setHasSeenToggleAnimation(true)
      }
    }, 0)

    return () => clearTimeout(timerId)
  }, [isOpen])

  return children ? (
    <Transition
      nodeRef={nodeRef}
      in={isOpen}
      timeout={{ enter: 1000, exit: 300 }}
      onExited={() => {
        // setHasSeenToggleAnimation(true)
      }}
    >
      {(state: any) => (
        <Grid
          as="aside"
          className="sidenav"
          aria-expanded={isOpen}
          data-testid="SecondarySidebar"
          gridArea="sidebar"
          style={{
            ...panelTransition[state],
          }}
          marginRight="-12px"
        >
          <Flex
            opacity={1}
            width={PANEL_WIDTH}
            height="100vh"
            position="absolute"
            {...panelTransition[state]}
            {...props}
          >
            <Box
              borderRight="1px solid"
              borderColor="presets.neutral.25"
              _dark={{ borderColor: '#05655F' }}
              overflowX="hidden"
              marginLeft="-1px"
              height="100vh"
            >
              {children}
            </Box>
            <Flex marginLeft="auto" alignItems="center" position="relative">
              <Box
                data-testid="SecondarySidebarToggle"
                cursor="pointer"
                background="presets.turquoise.20"
                width="12px"
                height={
                  isToggleButtonSmall && (isOpen || !hasSeenToggleAnimation)
                    ? '100px'
                    : '67vh'
                }
                onClick={toggleExpand}
                borderRadius="0 12px 12px 0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                transition="all ease 150ms"
                {...buttonTransition[state]}
                {...(!hasSeenToggleAnimation && {
                  ...buttonTransition[state],
                })}
              >
                {isOpen ? (
                  <DoubleChevronIcon color="presets.turquoise.80" w="9px" />
                ) : (
                  <DoubleChevronIcon
                    transform="scaleX(-1)"
                    color="presets.turquoise.80"
                    w="9px"
                  />
                )}
              </Box>
            </Flex>
          </Flex>
        </Grid>
      )}
    </Transition>
  ) : null
}
